import { createColumnHelper, Row } from "@tanstack/react-table";
import { JSX } from "react";

import { ButtonTypes, CreateTableStyle, ListData_ValueTypes, SelectFieldOption_Values } from "../../../Definitions";
import { colors, glyphs, useUnitsComponent } from "../../../Styles";

import { EditAbleCell } from "../CellTypes/styledTableCell";
import { HeadCell } from "../CellTypes/headerTableCell";
import { StringTableCell } from "../CellTypes/stringTableCell";
import { NumberTableCell } from "../CellTypes/numberTableCell";
import { CheckboxTableCell } from "../CellTypes/checkboxTableCell";
import { ListTableCell } from "../CellTypes/listTableCell";
import { GenericColumnConstructorProps, GenericDataParameters, GenericRow, SelectFieldTableOption } from "./useColumnConstructor";
import { DivTableCell } from "../CellTypes/divTableCell";
import { CreatableListTableCell } from "../CellTypes/creatableListTableCell"
import { Button } from "../../BaseComponents";
import { ProgressTableCell } from "../CellTypes/progressTableCell";
import { DisplayCell } from "../CellTypes/displayTableCell";
import { ButtonTableCell } from "../CellTypes/buttonTableCell";
import { ValueSteps } from "../../BaseComponents/newInput";

export interface EditDataParameters<T> extends GenericDataParameters<T> {
  staticColumn?: boolean,
  options?: SelectFieldTableOption<T>,
  onClick?: () => void;
  valueStep?: ValueSteps
}

interface EditableColumnConstructorProps<T> extends Omit<GenericColumnConstructorProps, "accessorData"> {
  accessorData: EditDataParameters<T>[]
} 

export const useEditableColumnConstructor = <T extends GenericRow,> ({accessorData, tableStyle, canEdit}: EditableColumnConstructorProps<T> & EditAbleCell ) => {
  const columnHelper = createColumnHelper<T>();
  const getUnit = useUnitsComponent();
  const {headCellStyle, bottomCellStyle} = CreateTableStyle(tableStyle)

  return accessorData.map(({accessor, unit, staticColumn=false, type, headerValue, idForColor, sort, options, width, divStyleOverride, styleOverrideOnValue, onClick, valueStep=ValueSteps.integers}) => {
    const footerValue = unit ? getUnit(unit) : "";
    switch (type) {
      default: 
      case ListData_ValueTypes.String:
        return columnHelper.accessor(
          row => (row[accessor] as string) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => StringTableCell<T>({...props, canEdit, staticColumn}),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "alphanumeric" : undefined,
            meta: {width: width},
            filterFn: "equalsString",
          }
        )
      case ListData_ValueTypes.Number:
        return columnHelper.accessor(
          row => (row[accessor] as number) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={onClick ? onClick : (!canEdit && sort ? props.column.getToggleSortingHandler() : undefined)} keyPass={props.header.id+"header"}/>),
            cell: (props) => NumberTableCell({...props, canEdit, staticColumn, valueStep}),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "basic" : undefined,
            meta: {width: width},
            sortDescFirst: false,
            filterFn: "equals"
          }
        )
      case ListData_ValueTypes.Check:
        return columnHelper.accessor(
        row => row[accessor] as boolean,
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => CheckboxTableCell({...props, canEdit, staticColumn}),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "basic" : undefined,
            meta: {width: width}
          }
        )
      case ListData_ValueTypes.List:
        return columnHelper.accessor(
          row => (row[accessor] as SelectFieldOption_Values) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => ListTableCell<T>({...props, canEdit, staticColumn, divStyleOverride, styleOverrideOnValue, options: options ? options : []}),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? (listItemA, listItemB, columnId) => {
              return ((listItemA.original[columnId] as SelectFieldOption_Values) || {label:""}).label.localeCompare(((listItemB.original[columnId] as SelectFieldOption_Values)|| {label: ""}).label )
            } : undefined,
            meta: {width: width},
            filterFn: (row, columnId, filterValue: SelectFieldOption_Values[]) => {
              const value = row.getValue<SelectFieldOption_Values>(columnId);
              return filterValue.map((filterParameter) => (filterParameter.id)).includes(value?.id as number)
            }
          }
        )
      case ListData_ValueTypes.CreatableList:
        return columnHelper.accessor(
          row => (row[accessor] as SelectFieldOption_Values) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => CreatableListTableCell<T>({...props, canEdit, staticColumn, divStyleOverride, styleOverrideOnValue, options: options ? options : []}),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "alphanumeric" : undefined,
            meta: {width: width},
            filterFn: (row, columnId, filterValue: SelectFieldOption_Values[]) => {
              const value = row.getValue<SelectFieldOption_Values>(columnId);
              return filterValue.map((filterParameter) => (filterParameter.id)).includes(value?.id as number)
            }
          }
        )
      case ListData_ValueTypes.Div:
        return columnHelper.accessor(
          row => (row[accessor] as JSX.Element) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: props => DivTableCell({...props, canEdit, staticColumn, divStyleOverride, styleOverrideOnValue}),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={""} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "alphanumeric" : undefined,
            meta: { 
              width: width,
             
            }
          }
        )
        case ListData_ValueTypes.Display:
          return columnHelper.accessor(
            row => (row[accessor] as string) ?? "",
            {
              id: accessor,
              header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
              cell: (props) => DisplayCell({...props, canEdit, staticColumn, divStyleOverride, styleOverrideOnValue}),
              footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
              sortingFn: sort ? "alphanumeric" : undefined,
              meta: {width: width},
            }
          )
        case ListData_ValueTypes.ProgressBar:
          return columnHelper.accessor(
            row => (row[accessor] as JSX.Element) ?? "",
            {
              id: accessor,
              header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
              cell: props => ProgressTableCell({...props, canEdit, staticColumn}),
              footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={""} keyPass={props.header.id+"footer"}/>),
              sortingFn: sort ? (rowA,rowB,columnId) => {
                console.log((rowA.original[columnId] as JSX.Element).props["progressed"])
                return (rowA.original[columnId] as JSX.Element).props["progressed"] - (rowB.original[columnId] as JSX.Element).props["progressed"]  
              } : undefined,
              meta: {width: width}
            }
          )
        case ListData_ValueTypes.Button:
          return columnHelper.accessor(
          row => row[accessor] as JSX.Element,
            {
              id: accessor,
              header: (props) => (<HeadCell pinningStyle={tableStyle} sortArrow={sort} {...props} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
              cell: (props) => ButtonTableCell({...props, canEdit, staticColumn }),
              footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
              sortingFn: sort ? "basic" : undefined,
              meta: {width: width},
            }
          ) 
      case ListData_ValueTypes.DeleteRowAction:
        const checkDeletePossible = (row: Row<T>, checkFunction?: (row: Row<T>) => boolean) => {
          if (checkFunction) {
            return checkFunction(row);
          }
          return true;
        }
        return columnHelper.display(
          { 
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} key={"removeRowActionHeader"} keyPass="removeRowActionHeader" style={headCellStyle} cellValue={""}/>),
            cell: (props) => {return ((canEdit && checkDeletePossible(props.row, props.table.options.meta?.allowDeleteRowCheck)) ? (<Button content={""} onClick={() => props.table.options.meta?.deleteRow(props.row.original.id)} buttonType={ButtonTypes.textButton} startEnhancer={glyphs.Delete({fillColor: colors.greyDarkSecondary, size: "20px"})}/>) :  (<div>{}</div>))},
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={"removeRowActionFooter"} keyPass="removeRowActionFooter" style={bottomCellStyle} cellValue={""}/>),
            meta: {
              width: width,
            }
          }
        )
      }
    }
  )
}